import * as y from "yup";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// Extend dayjs with timezone support
dayjs.extend(utc);
dayjs.extend(timezone);

export const AdSetFormSchema = y
  .object({
    id: y.string().nullable(),
    external_id: y.string().nullable(),
    name: y.string().required("Please provide a name for the adset"),
    parent_id: y.string(),
    campaign_type: y.string(),
    // TODO: enable and get it from campaign
    // account_id: y.string().required("Account ID is required"),
    account_id: y.string().optional(),
    platform: y.string().required("Platform is required"),
    timezone: y.string().required("Timezone is required"),

    budget_type: y.string().oneOf(["campaign", "adset"]),
    budget: y
      .object({
        type: y
          .string()
          // .required("Budget type is required")
          .oneOf(["daily", "lifetime"]),
        amount: y
          .object({
            amount: y.number().required("Budget amount is required").nullable(),
            currency: y.string().required("Currency is required"),
          })
          .required("Budget amount is required"),
      })
      .test("budget-type", "Budget is required", function (value) {
        console.log("budget-type", value, this.parent.budget_type);
        return this.parent.budget_type === "adset"
          ? value.amount?.amount > 0
          : true;
      }),

    // Schedule - flat structure for form
    start_time: y
      .string()
      .test(
        "is-valid-date",
        "Invalid date format",
        (value) => !value || !isNaN(Date.parse(value))
      )
      .test(
        "is-future",
        "Start time must be in the future",
        (value, context) => {
          if (!value) return true;

          // For existing adsets, allow past start times (backend will handle validation)
          const isExistingAdset = context.parent.id;
          if (isExistingAdset) {
            return true;
          }

          // For new adsets, start time must be in the future
          const timezone = context.parent.timezone;
          if (!timezone) return true; // If no timezone, fall back to basic validation

          const startTimeInTimezone = dayjs.tz(value, timezone);
          const nowInTimezone = dayjs.tz(new Date(), timezone);

          return startTimeInTimezone.isAfter(nowInTimezone);
        }
      ),
    stop_time: y
      .string()
      .nullable()
      .test(
        "is-valid-date",
        "Invalid date format",
        (value) => !value || !isNaN(Date.parse(value))
      )
      .test(
        "is-after-start",
        "Stop time must be after start time",
        function (value) {
          const startTime = this.parent.start_time;
          if (!value || !startTime) return true;
          return new Date(value) > new Date(startTime);
        }
      ),

    // Targeting - flat structure for form
    age_min: y.number().nullable().min(13, "Minimum age must be at least 13"),
    age_max: y.number().nullable().max(99, "Maximum age must be at most 99"),
    genders: y.string().nullable(),
    countries: y
      .array()
      .of(
        y.object({
          key: y.string().nullable(),
          value: y.string().nullable(),
        })
      )
      .default([]),
    geo_markets: y
      .array()
      .of(
        y.object({
          key: y.string().required(),
          name: y.string(),
          country: y.string(),
          value: y.string().required(),
        })
      )
      .default([]),
    regions: y
      .array()
      .of(
        y.object({
          key: y.string().required(),
          value: y.string().required(),
        })
      )
      .default([]),
    cities: y
      .array()
      .of(
        y.object({
          key: y.string().required(),
          value: y.string().required(),
        })
      )
      .default([]),
    interests: y
      .array()
      .of(
        y.object({
          key: y.string().required(),
          value: y.string().required(),
        })
      )
      .default([]),
    device_platforms: y
      .array()
      .of(
        y.object({
          key: y.string().required(),
          value: y.string().required(),
        })
      )
      .default([]),

    // Placements - flat structure for form
    facebook_positions: y.array().of(y.string()).default([]),
    instagram_positions: y.array().of(y.string()).default([]),

    // custom_audiences: y.array().of(y.object()).default([]),

    custom_audiences: y
      .array()
      .of(
        y.object({
          id: y.string().nullable(),
          name: y.string().nullable(),
          audience_type: y
            .string()
            .oneOf(["kyd", "csv", "page_lookalike", "page_engagers", "meta"])
            .required(),
          description: y.string().nullable(),
          created_at: y.string().nullable(),
          // CSV specific fields
          file_key: y.string().nullable(),
          mapping: y.array().of(y.string()).nullable(),
          schema_fields: y.array().of(y.string()).nullable(),
          // Page lookalike specific fields
          page_type: y.string().oneOf(["facebook", "instagram"]).nullable(),
          page_id: y.string().nullable(),
          page_name: y.string().nullable(),
          lookalike_ratio: y.string().nullable(),
          // KYD specific fields
          genres: y
            .array()
            .of(
              y.object({
                key: y.string().nullable(),
                value: y.string().nullable(),
              })
            )
            .nullable(),
          artists: y
            .array()
            .of(
              y.object({
                key: y.string().nullable(),
                value: y.string().nullable(),
              })
            )
            .nullable(),
          lookalike: y
            .object({
              name: y.string().nullable(),
              ratio: y.string().nullable(),
            })
            .nullable(),
          // Meta specific fields
          subtype: y.string().nullable(),
          time_created: y.string().nullable(),
          time_updated: y.string().nullable(),
          data_source: y
            .object({
              type: y.string().nullable(),
              sub_type: y.string().nullable(),
            })
            .nullable(),
          delivery_status: y
            .object({
              code: y.number().nullable(),
              description: y.string().nullable(),
            })
            .nullable(),
          approximate_count_lower_bound: y.number().nullable(),
          approximate_count_upper_bound: y.number().nullable(),
        })
      )
      .default([]),

    audience_type: y.string().oneOf(["advantage", "manual"]),

    // Tracking
    pixel_id: y.string().nullable(),
    custom_conversion_event: y.boolean(),

    // Legacy fields for backward compatibility
    optimization_goal: y
      .object({
        value: y.string().required("Please select an optimization goal"),
        label: y.string().required(),
      })
      .nullable(),
    targeting_query: y.string().default(""),
    targeting_type: y
      .object({
        value: y.string().required("Please select a targeting type"),
        label: y.string().required(),
      })
      .nullable(),
    targeting_subtype: y
      .object({
        value: y.string(),
        label: y.string(),
      })
      .nullable(),
    audience_ids: y
      .array()
      .of(
        y.object({
          value: y.string().required(),
          label: y.string().required(),
        })
      )
      .default([]),
  })
  .test(
    "age-range",
    "Minimum age must be less than maximum age",
    function (value) {
      if (value.age_min && value.age_max) {
        return value.age_min <= value.age_max;
      }
      return true;
    }
  );

// Type for the form data that matches the schema
export type AdSetFormData = y.InferType<typeof AdSetFormSchema>;
